import React from "react";
import ServicePageShell from "../../components/ServicePageShell/ServicePageShell";
import { useIsDanish } from "../../i18n/LanguageContext";

const AIAutomationServices = () => {
  const isDa = useIsDanish();
  const aiServices = [
    {
      title: "Private AI Chatbots",
      desc: "Custom AI chatbots running in your Azure tenant, trained only on your data — not public models.",
      examples: [
        "Internal knowledge assistant",
        "IT / HR / policy bots",
        "Developer documentation bots",
      ],
    },
    {
      title: "Secure AI Search (RAG)",
      desc: "ChatGPT-like experience where answers come only from approved internal sources.",
      examples: [
        "Search company documents & manuals",
        "Ask questions across SharePoint, Blob, SQL",
        "Source-cited AI responses",
      ],
    },
    {
      title: "AI-Powered Process Automation",
      desc: "Combine AI with workflows to automate decisions, summaries, and responses.",
      examples: [
        "Ticket summarization & categorization",
        "Email & request triage",
        "Draft responses with human approval",
      ],
    },
    {
      title: "AI Bots for IT & Operations",
      desc: "Operational AI assistants that help teams understand systems and incidents faster.",
      examples: [
        "Explain alerts & logs",
        "Deployment & incident summaries",
        "Operational Q&A bots",
      ],
    },
    {
      title: "AI Governance & Safety",
      desc: "AI implementations with security, auditing, and guardrails built in from day one.",
      examples: [
        "Role-based AI access (Entra ID)",
        "Prompt filtering & logging",
        "Usage monitoring & audit trails",
      ],
    },
    {
      title: "Custom AI Integrations",
      desc: "Embed AI capabilities directly into your applications, portals, and APIs.",
      examples: [
        "AI inside internal apps",
        "API-based AI services",
        "AI-enhanced workflows",
      ],
    },
  ];

  const aiStack = [
    "Azure OpenAI",
    "Azure AI Search",
    "Azure Functions",
    "Azure Container Apps",
    "Microsoft Entra ID",
    "Blob Storage & SQL",
    "API Management",
    "Azure Monitor & Logs",
  ];

  const whyAI = [
    {
      title: "Your data stays private",
      desc: "No data sent to public AI platforms or used to train external models.",
    },
    {
      title: "Enterprise-grade security",
      desc: "Identity, network isolation, logging, and access control built in.",
    },
    {
      title: "Useful, not gimmicky",
      desc: "AI that solves real problems — not just demos.",
    },
    {
      title: "Controlled & auditable",
      desc: "You always know who used AI, how, and why.",
    },
  ];
  const aiServicesDa = [
    ["Private AI-chatbots", "Custom AI-chatbots i jeres Azure tenant, bygget på jeres data og adgangsmodel.", ["Intern knowledge assistant", "IT-, HR- og policy-bots", "Bots til udviklerdokumentation"]],
    ["Sikker AI Search (RAG)", "ChatGPT-lignende oplevelse hvor svar kommer fra godkendte interne kilder.", ["Søg i dokumenter og manualer", "Spørg på tværs af SharePoint, Blob og SQL", "AI-svar med kildehenvisninger"]],
    ["AI-drevet procesautomation", "Kombinér AI med workflows for at automatisere beslutninger, opsummeringer og svar.", ["Ticket summary og kategorisering", "E-mail og request triage", "Svarudkast med human approval"]],
    ["AI-bots til IT og drift", "Operationelle AI-assistenter der hjælper teams med at forstå systemer og incidents hurtigere.", ["Forklar alerts og logs", "Deployment- og incident summaries", "Operationelle Q&A bots"]],
    ["AI governance og safety", "AI-implementeringer med sikkerhed, audit og guardrails indbygget fra dag ét.", ["Role-based AI access", "Prompt filtering og logging", "Usage monitoring og audit trails"]],
    ["Custom AI-integrationer", "Indbyg AI direkte i jeres applikationer, portaler og API'er.", ["AI i interne apps", "API-baserede AI-services", "AI-forbedrede workflows"]],
  ].map(([title, desc, examples]) => ({ title, desc, examples }));
  const whyAIDa = [
    { title: "Jeres data forbliver private", desc: "Ingen data sendes til offentlige AI-platforme eller bruges til at træne eksterne modeller." },
    { title: "Enterprise-sikkerhed", desc: "Identitet, netværksisolering, logging og adgangskontrol er bygget ind." },
    { title: "Nyttigt, ikke gimmick", desc: "AI der løser reelle problemer, ikke kun demoer." },
    { title: "Kontrolleret og auditerbart", desc: "I ved hvem der brugte AI, hvordan og hvorfor." },
  ];
  const currentAiServices = isDa ? aiServicesDa : aiServices;
  const currentWhyAI = isDa ? whyAIDa : whyAI;

  return (
    <ServicePageShell>
      <div className="ai-wrap">
        {/* HERO */}
        <section className="ai-hero">
          <div className="ai-heroInner">
            <div className="ai-heroText">
              <div className="ai-pill">
                <span className="ai-dot" /> AI Automation & AI Bots
              </div>

              <h1 className="ai-title">
                {isDa ? "Sikre AI-bots bygget på jeres data i jeres cloud." : "Secure AI bots — built on your data, inside your cloud."}
              </h1>

              <p className="ai-subtitle">
                {isDa ? "Vi designer og bygger private AI-assistenter og automation, så jeres data forbliver sikre, compliant og under jeres kontrol." : "We design and build private AI assistants and automation using Microsoft Azure — so your data stays secure, compliant, and under your control."}
              </p>

              <div className="ai-ctaRow">
                <a className="ai-btnPrimary" href="/#contact-section">
                  {isDa ? "Tal om AI" : "Talk AI"} <span aria-hidden="true">→</span>
                </a>
                <a className="ai-btnGhost" href="/use-cases">
                  {isDa ? "Se cases" : "View use cases"}
                </a>
              </div>

              <div className="ai-trust">
                <div className="ai-trustItem">
                  <strong>{isDa ? "Privat" : "Private"}</strong>
                  <span>{isDa ? "Kun jeres data" : "Your data only"}</span>
                </div>
                <div className="ai-divider" />
                <div className="ai-trustItem">
                  <strong>{isDa ? "Sikker" : "Secure"}</strong>
                  <span>Enterprise-grade</span>
                </div>
                <div className="ai-divider" />
                <div className="ai-trustItem">
                  <strong>{isDa ? "Praktisk" : "Practical"}</strong>
                  <span>{isDa ? "Reelle use cases" : "Real use cases"}</span>
                </div>
              </div>
            </div>

            <div className="ai-heroCard">
              <h3>{isDa ? "Hvad virksomheden får" : "What businesses get"}</h3>
              <ul className="ai-list">
                <li>✔ {isDa ? "AI uden datalæk" : "AI without data leakage"}</li>
                <li>✔ {isDa ? "Hurtigere beslutninger og svar" : "Faster decisions & responses"}</li>
                <li>✔ {isDa ? "Mindre manuelt arbejde" : "Less manual work"}</li>
                <li>✔ {isDa ? "Kontrol og auditability" : "Full control & auditability"}</li>
              </ul>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="ai-section">
          <div className="ai-sectionHead">
            <h2>{isDa ? "AI-services vi leverer" : "AI services we provide"}</h2>
            <p>
              {isDa ? "Praktiske AI-løsninger bygget til reelle forretningsbehov, med adgang, data og audit tænkt ind fra start." : "Practical AI solutions built on Microsoft technologies — designed for real business needs."}
            </p>
          </div>

          <div className="ai-grid">
            {currentAiServices.map((a) => (
              <div key={a.title} className="ai-card">
                <h3>{a.title}</h3>
                <p>{a.desc}</p>
                <ul>
                  {a.examples.map((e) => (
                    <li key={e}>→ {e}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* STACK */}
        <section className="ai-section ai-stack">
          <h2>{isDa ? "AI stack" : "Microsoft AI stack"}</h2>
          <div className="ai-stackGrid">
            {aiStack.map((t) => (
              <span key={t} className="ai-chip">
                {t}
              </span>
            ))}
          </div>
        </section>

        {/* WHY */}
        <section className="ai-section ai-why">
          <h2>{isDa ? "Hvorfor privat AI betyder noget" : "Why private AI matters"}</h2>
          <div className="ai-whyGrid">
            {currentWhyAI.map((w) => (
              <div key={w.title} className="ai-whyCard">
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="ai-section ai-final">
          <div className="ai-finalCard">
            <h2>{isDa ? "Vil I bruge AI uden at miste kontrollen?" : "Want AI without losing control?"}</h2>
            <p>
              {isDa ? "Vi hjælper jer med at designe sikker og brugbar AI, tilpasset jeres data, brugere og sikkerhedskrav." : "We help you design safe, useful AI — tailored to your data, your users, and your security requirements."}
            </p>
            <a className="ai-btnPrimary" href="/#contact-section">
              {isDa ? "Start sikkert" : "Start securely"} <span aria-hidden="true">→</span>
            </a>
          </div>
        </section>

        {/* CSS */}
        <style>{`
          /* identical structure & polish as other service pages */
        .ai-wrap{
  max-width: 1100px;
  margin: 0 auto;
  padding: 28px 18px 70px;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Apple Color Emoji", "Segoe UI Emoji";
  color: #0f172a;
}

/* HERO */
.ai-hero{
  position: relative;
  border-radius: 28px;
  padding: 34px 20px;
  overflow: hidden;
  background:
    radial-gradient(1200px 500px at 10% 10%, rgba(59,130,246,0.18), transparent 60%),
    radial-gradient(900px 420px at 90% 10%, rgba(168,85,247,0.16), transparent 58%),
    linear-gradient(180deg, #ffffff, #f8fafc);
  border: 1px solid rgba(15,23,42,0.08);
  box-shadow:
    0 20px 60px rgba(2, 6, 23, 0.10),
    0 2px 12px rgba(2, 6, 23, 0.06);
}

.ai-heroInner{
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 22px;
  align-items: center;
  position: relative;
  z-index: 2;
}

@media (max-width: 900px){
  .ai-heroInner{ grid-template-columns: 1fr; }
}

.ai-pill{
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.04);
  border: 1px solid rgba(15,23,42,0.08);
  font-weight: 800;
  font-size: 0.95rem;
}

.ai-dot{
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, #3b82f6, #a855f7);
  box-shadow: 0 0 0 3px rgba(59,130,246,0.14);
}

.ai-title{
  margin: 14px 0 10px;
  font-size: clamp(2rem, 2.6vw, 3.1rem);
  line-height: 1.06;
  letter-spacing: -0.02em;
}

.ai-subtitle{
  margin: 0 0 18px;
  font-size: 1.08rem;
  line-height: 1.6;
  color: rgba(15,23,42,0.78);
  max-width: 62ch;
}

.ai-ctaRow{
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

/* BUTTONS */
.ai-btnPrimary{
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 14px;
  text-decoration: none;
  font-weight: 900;
  color: #fff;
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
  border: 1px solid rgba(255,255,255,0.22);
  transition: transform .15s ease, box-shadow .15s ease;
}

.ai-btnPrimary:hover{
  transform: translateY(-1px);
  box-shadow: 0 18px 40px rgba(37,99,235,0.22);
}

.ai-btnGhost{
  padding: 12px 16px;
  border-radius: 14px;
  font-weight: 900;
  text-decoration: none;
  color: #0f172a;
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(15,23,42,0.10);
  backdrop-filter: blur(8px);
}

/* TRUST */
.ai-trust{
  display: inline-flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(255,255,255,0.70);
  border: 1px solid rgba(15,23,42,0.08);
}

.ai-trustItem{
  display: grid;
  line-height: 1.1;
}

.ai-trustItem span{
  font-size: 0.9rem;
  color: rgba(15,23,42,0.7);
}

.ai-divider{
  width: 1px;
  height: 28px;
  background: rgba(15,23,42,0.12);
}

/* HERO CARD */
.ai-heroCard{
  border-radius: 22px;
  background: rgba(255,255,255,0.70);
  border: 1px solid rgba(15,23,42,0.10);
  box-shadow: 0 18px 50px rgba(2,6,23,0.10);
  padding: 16px;
}

.ai-list{
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
}

/* SECTIONS */
.ai-section{ margin-top: 34px; }

.ai-sectionHead{
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
}

.ai-sectionHead h2{
  font-size: 1.7rem;
  margin: 0;
}

.ai-sectionHead p{
  color: rgba(15,23,42,0.72);
}

/* SERVICE GRID */
.ai-grid{
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

@media (max-width: 760px){
  .ai-grid{ grid-template-columns: 1fr; }
}

.ai-card{
  border-radius: 22px;
  background: white;
  border: 1px solid rgba(15,23,42,0.08);
  padding: 16px;
  box-shadow: 0 12px 36px rgba(2,6,23,0.06);
}

.ai-card h3{ margin: 0 0 8px; }
.ai-card p{ color: rgba(15,23,42,0.72); }

.ai-card ul{
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
  display: grid;
  gap: 8px;
}

.ai-card li{
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(15,23,42,0.08);
}

/* STACK */
.ai-stack{
  border-radius: 26px;
  padding: 22px;
  background:
    radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.12), transparent 55%),
    radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.10), transparent 55%);
}

.ai-stackGrid{
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.ai-chip{
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(15,23,42,0.10);
  font-weight: 800;
}

/* WHY */
.ai-whyGrid{
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

@media (max-width: 760px){
  .ai-whyGrid{ grid-template-columns: 1fr; }
}

.ai-whyCard{
  border-radius: 18px;
  padding: 14px;
  background: white;
  border: 1px solid rgba(15,23,42,0.08);
}

/* FINAL CTA */
.ai-finalCard{
  border-radius: 26px;
  padding: 22px;
  background:
    radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.10), transparent 55%),
    radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.09), transparent 55%);
}

        `}</style>
      </div>
    </ServicePageShell>
  );
};

export default AIAutomationServices;
